import Immutable from 'immutable';
import mergeDeep from 'deepmerge';

import { get } from 'ctm-util-functions';

import { containerStateKeys } from 'ctm-components';

import { FormSchemaResolverModel, fieldSetHasReferences } from './resolver_model';
import { FormSchemaAccessModel } from './schema_access_model';

/**
 * A simple wrapper around a form schema to provide higher level access.
 */
class FormSchemaModel {

    /**
     * Initialize and defined the form schema and model
     * @param {Object} rulesModel - The RulesModel instance
     * @param {Object} overrideSchemas - The form schema overrides object which provides new content etc if this app is for a whitelabel or a competition
     * @param {Array} formSchemas - The form schema objects
     */
    constructor(rulesModel, overrideSchemas, ...formSchemas) {
        this.rulesModel = rulesModel;
        this.overrideSchemas = overrideSchemas;
        this._formSchemas = formSchemas;
        this.accessModel = new FormSchemaAccessModel(...formSchemas);
        this.resolverModel = new FormSchemaResolverModel(this.rulesModel, ...formSchemas);

        this.pages = this.accessModel.getPages();
        this.properties = this.getProperties();
        // cache identifiers to throw a warning when we receive a duplicate
        this.identifierCache = {};
        ({ elements: this.allElements, fieldSets: this.allFieldSets } = this._allElements());

        this.valueMap = this.getValueMap();
        this.labelValueMaps = this.getIDLabelMap();
        // grab the extra form stateKeys that don't have corresponding form fields and create convenience objects that
        // map the stateKeys to an 'identifier' key. These are only used to set the initial form state.
        this.extraFormStateKeys = {};
        for (const stateKey of Object.keys(this.properties)) {
            if (this.properties[stateKey].extraFormStateKeys) {
                this.extraFormStateKeys[stateKey] = Object.keys(this.properties[stateKey].extraFormStateKeys || {}).reduce((ret, cur) => ret.concat([{ identifier: cur }]), []);
            }
        }
    }

    /**
     * Return all page objects from supplied form schemas
     * @returns {Object} - write a description
     */
    getProperties = () => {
        const props = {};
        for (const schema of this._formSchemas) {
            const stateKey = schema.formProperties.stateKey;
            if (!props[stateKey]) {
                props[stateKey] = schema.formProperties;
            } else {
                props[stateKey] = Object.assign(props[stateKey], schema.formProperties);
            }
        }
        return props;
    };

    /**
     * Check if the element has a corresponding containerStateKeys function
     * @param {Object} element - The element to check
     * @returns {boolean} - True if there is a containerStateKey function for the element's field.type
     */
    elementHasContainerStateKeys = (element) => {
        return Object.keys(containerStateKeys).includes(get(element, 'field.type'));
    };

    /**
     * When extending components, we don't want array props to be concatenated. We just want the source array
     * @see EVT-1882
     * @param {Array} destinationArray The original array
     * @param {Array} sourceArray The latest array in the merge
     * @returns {Array} An array
     * @private
     */
    _useSourceArray = (destinationArray, sourceArray) => sourceArray;

    /**
     * Iterates over the entire formSchema and flattens out the elements into an array
     * @todo: this is getting a bit messy.....
     * @private
     * @returns {Object} - All elements and all field sets
     */
    _allElements = () => {
        // Get a list of all top level form elements for all pages from schema
        const allElements = {};
        const allFieldSets = {};
        const currentElements = Object.keys(this.pages).map((stateKey) => [stateKey, this.pages[stateKey]])
            .reduce((elements, [stateKey, pages]) => {
                if (!elements[stateKey]) {
                    // eslint-disable-next-line no-param-reassign
                    elements[stateKey] = [];
                }
                for (const page of pages) {
                    if (!page.elements) {
                        continue;
                    }
                    elements[stateKey].push(...page.elements);
                }
                return elements;
            }, {});

        for (const stateKey of Object.keys(currentElements)) {
            allElements[stateKey] = [];
            allFieldSets[stateKey] = [];
            const currentElementsForStateKey = currentElements[stateKey];
            while (currentElementsForStateKey.length > 0) {
                let el = currentElementsForStateKey.pop();
                el.stateKey = stateKey;
                const whiteLabelOverride = this.overrideSchemas[el.identifier] || {};
                if (Object.keys(whiteLabelOverride).length) {
                    el = mergeDeep(el, whiteLabelOverride, { arrayMerge: this._useSourceArray });
                }

                if (el.elType === 'fieldSet' && !fieldSetHasReferences(el)) {
                    // el is fieldSet, it contains elements, add these to currentElements for future processing
                    currentElementsForStateKey.push(...el.elements);
                    // set the form stateKey on element for easy access
                    allFieldSets[stateKey].push(el);
                } else if (this.elementHasContainerStateKeys(el)) {
                    currentElementsForStateKey.push(...containerStateKeys[el.field.type](el));
                } else if (el.elType !== 'fieldSet' && !el.reference) {
                    // Sanity check so we can catch any ill-formed elements
                    if (el.identifier === undefined) {
                        throw Error(`No element identifier for element: ${el}`);
                    }
                    if (this.identifierCache[el.identifier] && !el.identifierCategory) {
                        // eslint-disable-next-line no-console
                        console.warn(`Found a duplicate identifier for ${el.identifier} with no identifierCategory`);
                    }
                    this.identifierCache[el.identifier] = true;

                    // if the identifier looks like "identifier": "ignore.xxx", then we shouldn't add it to the state
                    if (!el.identifier.includes('ignore.')) {
                        // set the form stateKey on element for easy access
                        allElements[stateKey].push(el);
                    }

                    // el is fieldRow, it MAY contain elements (e.g. toggle), add these to currentElements for future processing
                    if (el.elements) {
                        currentElementsForStateKey.push(...el.elements);
                    }
                }
            }
        }

        const { resolvedElements, resolvedFieldSets } = this.resolverModel.allResolvedElements();
        return {
            elements: mergeDeep(allElements, resolvedElements, { arrayMerge: (dst, src) => dst.concat(src) }),
            fieldSets: mergeDeep(allFieldSets, resolvedFieldSets, { arrayMerge: (dst, src) => dst.concat(src) })
        };
    };

    /**
     * Get the map of values to labels for badgegroups with values and selects. Used to map backend enums to
     * readable values.
     * @returns {Object} - The map of values to labels
     */
    getValueMap = () => {
        const valueMap = {};
        const mappableFields = ['badgegroup', 'select'];
        for (const stateKey of Object.keys(this.allElements)) {
            valueMap[stateKey] = {};
            for (const element of this.allElements[stateKey]) {
                if (!element.field || (!mappableFields.includes(element.field.type))) {
                    continue;
                }
                const options = element.field.values || element.field.options;
                if (!options || !options.length) {
                    continue;
                }
                for (const option of options) {
                    valueMap[stateKey][option.value] = option.innerLabel || option.label;
                }
            }
        }
        return valueMap;
    };

    /**
     * Prepares a key value pair mapping of form label : value's. Used to map backend enums to
     * readable values.
     * @returns {Object} - The map of values to labels
     */
    getIDLabelMap = () => {
        const valueMap = {};
        const mappableFields = ['badgegroup', 'select'];
        for (const stateKey of Object.keys(this.allElements)) {
            valueMap[stateKey] = {};
            for (const element of this.allElements[stateKey]) {
                const labels = {};
                if (!element.field || (!mappableFields.includes(element.field.type))) {
                    continue;
                }
                const options = element.field.values || element.field.options;
                const identifier = element.identifier.split('.').slice(-1)[0];
                if (!options || !options.length) {
                    valueMap[stateKey][identifier] = element.field.label;
                } else {
                    for (const option of options) {
                        if (option.value) {
                            labels[option.value] = option.innerLabel || option.label;
                        }
                    }
                    valueMap[stateKey][identifier] = labels;
                }
            }
        }
        return valueMap;
    };

    /**
     * Return the default value for s form element, based on schema defaults, extraFormStateKey defaults or dynamicDefaults
     * @param {Object} element - The form element to get the default value for
     * @param {String} formStateKey - The stateKey of the form that the element belongs to
     * @param {*} defaultValue - The default, defaultValue
     * @returns {*} - The default value for the form element
     */
    getDefaultFieldValue = (element, formStateKey, defaultValue = null) => {
        const extraStateKeys = this.properties[formStateKey].extraFormStateKeys;
        const identifier = element.backendIdentifier || element.identifier;

        if (element.field && element.field.default) {
            return element.field.default;
        }

        if (extraStateKeys && extraStateKeys[identifier]) {
            // can't use get() here as identifier may contain dots
            return extraStateKeys[identifier];
        }
        return defaultValue;
    };

    /**
     * Creates a simple state object that maps element identifiers to a default value.
     * This allows for any number of levels of nesting
     * eg [a.b.c.d, a.b.c.e]
     * results in
     * {a: {b: {c: {d: null, e: null}}}}
     *
     * We need this as we generate the stateModel from the identifiers in the form schema and we're trying
     * to use the same model for the form as the backend to avoid transformations etc
     * The backend model can be arbitrarily nested so we need to be able to create an object easily that matches this.
     *
     * Warning: here be magic..... modify carefully
     *
     * @param {String} formStateKey - the stateKey to get the form state model for
     * @returns {Object} - The initial stateModel that represents formSchema
     */
    formStateModel = (formStateKey) => {
        // eslint-disable-next-line new-cap
        let stateModel = Immutable.Map();

        const allElements = [].concat(this.allElements[formStateKey], this.extraFormStateKeys[formStateKey]);
        for (const el of allElements) {
            if (el === undefined) {
                throw new Error(`Form Schema ${formStateKey} is not setup correctly`);
            }
            const identifier = el.backendIdentifier || el.identifier;
            const parts = identifier.split('.');
            // disable the rule here till I figure out how to make this work without multiple declarations.
            // eslint-disable-next-line one-var-declaration-per-line, one-var
            let obj, o = obj = {};
            parts.forEach(
                (key, index) => {
                    if (index === parts.length - 1) {
                        o = o[key] = this.getDefaultFieldValue(el, formStateKey);
                    } else {
                        o = o[key] = {};
                    }
                });
            stateModel = stateModel.mergeDeep(Immutable.fromJS(obj));
        }
        return stateModel;
    };

    /**
     * Maps FieldSet and FieldRow identifiers to their visibility status.
     * @param {String} formStateKey - The stateKey to get the visibility state model for
     * @returns {Object} - A mapping of identifier to visibility status for all FieldSets and the FieldRows that have
     * visibility rules defined
     */
    visibilityStateModel = (formStateKey) => {
        const stateModel = {};
        for (const element of this.allElements[formStateKey]) {
            if (!element.rules) {
                continue;
            }
            const elHasRule = element.rules.filter((rule) => rule.controls === 'visibility').length > 0;
            if (elHasRule) {
                // elements with visibility rules are not visible by default
                // @todo: change this to add a default value for visibility for more flexibility
                stateModel[element.identifier] = false;
            }
        }
        for (const fieldSet of this.allFieldSets[formStateKey]) {
            if (!fieldSet.rules) {
                stateModel[fieldSet.identifier] = true;
            } else {
                // fieldSets with visibility rules are not visible by default
                stateModel[fieldSet.identifier] = !fieldSet.rules.filter((rule) => rule.controls === 'visibility').length > 0;
            }
        }
        // eslint-disable-next-line new-cap
        return Immutable.Map(stateModel);
    };

    /**
     * @desc Get page Object
     * @param {String} pageName - the desired page name
     * @returns {Object} - the desired page
     */
    getPage = (pageName) => {
        for (const stateKey of Object.keys(this.pages)) {
            try {
                const page = this.pages[stateKey].find((obj) => {
                    return obj.name === pageName;
                });
                if (!page) {
                    continue;
                }
                page.stateKey = stateKey;
                if (page.hasReferences === undefined || page.hasReferences === false) {
                    return page;
                }

                return this.resolverModel.resolvePageReferences(page);
            } catch (ex) {
                if (window.Raven) {
                    window.Raven.captureException(ex, {
                        pageName
                    });
                }
                return null;
            }
        }
        return null; // keep eslint happy
    };

    /**
     * Get the identifiers for each element in the page that corresponds to pageName
     * @param  {String} pageName The name of the page to the element names for
     * @returns {Array}          The array of names of the elements in the page
     */
    getPageElements = (pageName) => {
        const page = this.getPage(pageName);
        if (!page) {
            return [];
        }
        if (!page.elements) {
            return [];
        }

        const allElements = [];
        const currentElements = [...page.elements];
        while (currentElements.length) {
            let currentElement = currentElements.shift();
            if (currentElement.elType !== 'fieldSet') {
                // @todo: we shouldn't have to do this here, we should be able to ask the model for the element with the schema override
                // applied, but we need this in asap and this will work in the meantime - from HNC-612 / HNC-613
                const whiteLabelOverride = this.overrideSchemas[currentElement.identifier] || {};
                if (Object.keys(whiteLabelOverride).length) {
                    currentElement = mergeDeep(currentElement, whiteLabelOverride, { arrayMerge: this._useSourceArray });
                }
                allElements.push(currentElement);
            }
            if (currentElement.elements) {
                currentElements.push(...currentElement.elements);
            } else if (this.elementHasContainerStateKeys(currentElement)) {
                currentElements.push(...containerStateKeys[currentElement.field.type](currentElement));
            }
        }
        return allElements;
    };

    /**
     * @desc Get the fieldsets for a page
     * @param  {String} pageName - the page name
     * @returns {Array} - an array of page elements grabbed from the schema
     */
    getPageFieldSets = (pageName) => {
        const page = this.getPage(pageName);
        return (page && page.elements) || [];
    };

    /**
     * Returns true if the page for pageName has elements that require validation
     * @param {String} pageName - The name of the page to check
     * @returns {Boolean} - True if the page has elements that require validation, false otherwise
     */
    pageHasValidation = (pageName) => {
        const elements = this.getPageElements(pageName);
        if (!elements.length) {
            return false;
        }
        for (const element of elements) {
            if (!element.rules) {
                continue;
            }
            const hasValidation = element.rules.find((rule) => rule.controls === 'validation');
            if (hasValidation) {
                return true;
            }
        }
        return false; // keep eslint happy
    };

    /**
     * Get the fieldSet for the supplied elementID, this will also check for and return fieldSets with resolved fieldRows
     * @param {String} id - The element ID
     * @returns {Object} - The fieldSet that is the parent of the element
     */
    getResolvedFieldSetForElement = (id) => {
        // @todo: this is awful
        for (const stateKey of Object.keys(this.allFieldSets)) {
            for (const fieldSet of this.allFieldSets[stateKey]) {
                for (const el of fieldSet.elements) {
                    if (el.identifier === id || el.backendIdentifier === id || id.startsWith(el.identifier)) {
                        return fieldSet;
                    }
                }
            }
        }
        return null;
    };

    /**
     * Get a 'processed' fieldSet schema from this.allFieldSets
     * @param {String} stateKey - The stateKey for the form
     * @param {String} identifier - The fieldSet identifier
     * @returns {Object} - The fieldSet schema
     */
    getFieldSet = (stateKey, identifier) => {
        return this.allFieldSets[stateKey].filter((fieldSet) => fieldSet.identifier === identifier)[0];
    };

    /**
     * Set a fieldSet's elements dynamically. This is used when we need to render a fieldSet with dynamic elements based on the result
     * of a payload from an api call.
     * @param {String} stateKey - The stateKey for the form
     * @param {String} identifier - The fieldSet identifier
     * @param {Array} elements - The array of element schemas to set on the FieldSet
     */
    setFieldSetElements = (stateKey, identifier, elements) => {
        for (const fieldSet of this.allFieldSets[stateKey]) {
            if (fieldSet.identifier === identifier) {
                fieldSet.elements = elements;
            }
        }
    };

    /**
     * Override a fieldSet's element, it is used for custom fieldRows where schema is prepared in component
     * @param {String} stateKey - The stateKey for the form
     * @param {String} identifier - The fieldSet identifier
     * @param {Object} element - The new element schema to replace the old one
     */
    overrideElementInFieldSet = (stateKey, identifier, element) => {
        const { elements = [] } = this.getFieldSet(stateKey, identifier);
        const idx = elements.findIndex((el) => el.identifier === element.identifier);
        if (idx >= 0) {
            elements[idx] = element;
        }
    };

    /**
     * Override a element in allElements, it is used for custom fieldRows where schema is prepared in component
     * @param {String} stateKey - The stateKey for the form
     * @param {Object} element - The new element schema to replace the old one
     */
    overrideElementInAllElements = (stateKey, element) => {
        const elements = this.allElements[stateKey];
        const idx = elements.findIndex((el) => {
            if (element.identifierCategory) {
                return el.identifier === element.identifier && el.identifierCategory === element.identifierCategory;
            }
            return el.identifier === element.identifier;
        });
        if (idx >= 0) {
            elements[idx] = element;
        }
    };

    /**
     * Get a 'processed' element schema from this.allElements
     * @param {String} stateKey - The stateKey for the form
     * @param {String} identifier - The element identifier
     * @param {String} identifierCategory - Category of the identifier to filter element when elements
     * share the same identifier in different pages, e.g. person.email in both contact and enquiry page
     * @returns {Object} - The element schema
     */
    getElement = (stateKey, identifier, identifierCategory) => {
        let element = this.allElements[stateKey].filter((elementSchema) => {
            if (identifierCategory) {
                return elementSchema.identifier === identifier && elementSchema.identifierCategory === identifierCategory;
            }
            return elementSchema.identifier === identifier;
        })[0];
        if (!element) {
            element = this.getFieldSet(stateKey, identifier);
        }
        return element;
    };

}

export { FormSchemaModel };
